import { ArrowDropDown, ArrowDropUp, FilterList } from '@mui/icons-material';
import { Badge } from '@mui/material';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  AutocompletePopover,
  Match,
  Show,
  Switch,
  type TextFieldProps,
} from '@side/pantry';
import { useRef, useState } from 'react';
import { DropdownLoadingIcon } from 'src/components/DropdownLoadingIcon';
import {
  TrackHomePageEvent,
  useTrackHomePageAnalytics,
} from '$analytics/useTrackAnalytics/domains/homePage';
import type { SubmitAsOption } from '../types';
import { DatesFilterContainer } from './DatesFilterContainer';
import { useFactsAndFiguresContext } from './FactsAndFiguresContext';

const AgentsFilterArrow = (props: { isAgentFilterOpen: boolean }) => {
  const { isAgentFilterOpen } = props;

  return isAgentFilterOpen ? <ArrowDropUp /> : <ArrowDropDown />;
};

export function TransactionStatusSnapshotFilters() {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [agentSearch, setAgentSearch] = useState('');
  const [isAgentFilterOpen, setIsAgentFilterOpen] = useState(false);
  const hasChangedSearchOnce = useRef(false);
  const {
    agents,
    agentOptions,
    filtersCount,
    isLoadingAgents,
    isMobile,
    onChangeAgents,
    onClearAgents,
    toggleModal,
  } = useFactsAndFiguresContext();
  const trackEvent = useTrackHomePageAnalytics();

  function closeDateFilter() {
    hasChangedSearchOnce.current = false;
    setAnchorEl(null);
    setIsAgentFilterOpen(false);
  }

  return (
    <Stack
      sx={{
        alignItems: 'center',
        flexDirection: 'row',
        gap: 0,
        justifyContent: 'space-between',
        px: { xs: 2, md: 0 },
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{ color: 'other.spaceCadet', paddingRight: 0.5 }}
      >
        Transaction Status Snapshot*
      </Typography>

      <Switch>
        <Match when={isMobile}>
          <Button
            onClick={toggleModal}
            sx={{
              backgroundColor: 'background.paper',
              border: 1.5,
              borderColor: 'other.outlineBorder',
              color: 'text.secondary',
              gap: 0.5,
              paddingX: 1.25,
              paddingY: 0.75,
            }}
          >
            <FilterList width={18} />
            Filters
            <Show when={filtersCount > 0}>
              <Chip
                label={filtersCount}
                size="small"
                sx={{
                  backgroundColor: 'secondary.main',
                  borderRadius: '50%',
                  color: 'common.white',
                  height: 20,
                  minWidth: 22,
                  '& .MuiChip-label': {
                    paddingX: 0.75,
                    paddingY: 0,
                  },
                }}
              />
            </Show>
          </Button>
        </Match>

        <Match when={!isMobile}>
          <Stack
            sx={{
              flexDirection: 'row',
              gap: 1,
            }}
          >
            <DatesFilterContainer />

            <Button
              color="utility"
              disabled={isLoadingAgents}
              endIcon={
                isLoadingAgents ? (
                  <DropdownLoadingIcon sx={{ mr: 0.5 }} />
                ) : (
                  <AgentsFilterArrow isAgentFilterOpen={isAgentFilterOpen} />
                )
              }
              onClick={(event) => {
                setAnchorEl(event.currentTarget);
                setIsAgentFilterOpen(true);
              }}
              variant="contained"
            >
              Agents
              <Show when={agents.length > 0}>
                <Badge
                  badgeContent={agents.length}
                  color="secondary"
                  variant="inline"
                  sx={{ pl: 0.5 }}
                />
              </Show>
            </Button>

            <AutocompletePopover
              AutocompleteProps={{
                inputValue: agentSearch,
                onInputChange: (_, value) => {
                  hasChangedSearchOnce.current = true;

                  setAgentSearch(value);
                },
                onChange: (_, newAgents, reason) => {
                  if (reason === 'clear') {
                    onClearAgents();
                  }

                  if (reason === 'removeOption') {
                    trackEvent(
                      TrackHomePageEvent.FactsAndFiguresAgentFilterDeselectClick,
                      {
                        agentId: (newAgents as SubmitAsOption[])
                          ?.map((a) => a.value)
                          .join(','),
                      },
                    );
                  }

                  if (reason === 'selectOption') {
                    trackEvent(
                      TrackHomePageEvent.FactsAndFiguresAgentFilterSelectClick,
                      {
                        agentId: (newAgents as SubmitAsOption[])
                          ?.map((a) => a.value)
                          .join(','),
                      },
                    );
                  }

                  if (
                    ['removeOption', 'selectOption'].includes(reason) &&
                    Array.isArray(newAgents)
                  ) {
                    onChangeAgents(newAgents as SubmitAsOption[]);
                  }
                },
                options: agentOptions,
                // https://residenetwork.atlassian.net/browse/CORE-12684
                // @ts-expect-error temporary holdover until we patch pantry
                renderInput: (params: TextFieldProps) => (
                  <TextField
                    {...params}
                    slotProps={{
                      ...params.slotProps,
                      input: {
                        ...params.slotProps?.input,
                        fullWidth: true,
                        placeholder: agents.length ? '' : 'Search for agents',
                        sx: {
                          '&.MuiInputBase-root': {
                            paddingLeft: '4px !important',
                          },
                          '& .MuiAutocomplete-input': {
                            px: '8px !important',
                            py: '4px !important',
                          },
                          '& .MuiChip-root': {
                            marginLeft: '8px',
                          },
                        },
                      },
                    }}
                    onBlur={() => {
                      if (hasChangedSearchOnce.current) {
                        trackEvent(
                          TrackHomePageEvent.FactsAndFiguresAgentSearchUpdate,
                          {
                            searchValue: agentSearch,
                          },
                        );
                      }
                    }}
                  />
                ),
                renderOption: (optionProps, option, { selected }) => {
                  const { label } = option;
                  const [displayName, ...teamNameSplit] = label.split('-');
                  const teamName = teamNameSplit.join('-');

                  return (
                    <Stack
                      {...optionProps}
                      component="li"
                      sx={{
                        alignItems: 'center',
                        flexDirection: 'row',
                        height: 42,
                        marginY: 0.25,
                        paddingLeft: '0 !important',
                        '&.MuiAutocomplete-option[aria-selected="true"]': {
                          backgroundColor: 'white !important',
                        },
                        '&.MuiAutocomplete-option[aria-selected="true"]:hover':
                          {
                            backgroundColor: (t) =>
                              `${t.palette.action.hover} !important`,
                          },
                      }}
                    >
                      <Checkbox checked={selected} />

                      <Typography sx={{ marginRight: 2 }} variant="body1">
                        {displayName}
                      </Typography>

                      <Typography
                        sx={{ color: 'text.secondary' }}
                        variant="body2"
                      >
                        {teamName}
                      </Typography>
                    </Stack>
                  );
                },
                value: agents,
              }}
              ClearButtonProps={{
                onClick: () => onClearAgents(),
              }}
              DoneButtonProps={{
                onClick: () => {
                  trackEvent(
                    TrackHomePageEvent.FactsAndFiguresAgentFilterDoneClick,
                    {
                      agentId: agents.map((a) => a.value).join(','),
                    },
                  );

                  closeDateFilter();
                },
              }}
              PopoverProps={{
                anchorEl,
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'right',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'right',
                },
                open: isAgentFilterOpen,
                onClose: () => closeDateFilter(),
                sx: {
                  '& .MuiAutocomplete-listbox': {
                    paddingTop: 1.5,
                  },
                },
              }}
            />
          </Stack>
        </Match>
      </Switch>
    </Stack>
  );
}
